import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ConfirmDeleteDialog } from '../admin/confirmDeleteDialog';
import { ApiRequestError } from '../api/apiRequestError';
import { deleteMyTest } from '../api/deleteMyTest';
import { getMyTests } from '../api/getMyTests';
import { getSessionCreator } from '../api/getSessionCreator';
import { postClaimTestByKey } from '../api/postClaimTestByKey';
import { postSignOut } from '../api/postSignOut';

type MyTests = Awaited<ReturnType<typeof getMyTests>>;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// /my — the signed-in creator's tests (spec §4).
//
// Signed-out is a first-class state here, not an error: this route is reachable
// from the landing page and someone arriving without a session should be offered
// sign-in, not shown a failure.
export function MyTestsScreen() {
  const [data, setData] = useState<MyTests | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimKey, setClaimKey] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MyTests['tests'][number] | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      // Ask who is signed in BEFORE asking for their tests. /api/session always
      // answers 200, whereas /api/my/tests answers 401 when signed out — and a
      // 401 is logged as a console error by the browser no matter how cleanly the
      // app handles it. Signed-out is the ordinary state on this screen, so it
      // should not leave an error in the console of every visitor who has not
      // signed in. It also saves a request that was always going to fail.
      const session = await getSessionCreator();
      if (!session.signed_in) {
        setSignedOut(true);
        return;
      }
      setData(await getMyTests());
      setSignedOut(false);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401) {
        setSignedOut(true);
        return;
      }
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load your tests.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const claim = async (event: FormEvent) => {
    event.preventDefault();
    if (claiming || !claimKey.trim()) return;
    setClaiming(true);
    setClaimError(null);
    setClaimed(null);
    try {
      const result = await postClaimTestByKey(claimKey.trim());
      setClaimed(result.title ?? result.test_id);
      setClaimKey('');
      await load();
    } catch (caught) {
      setClaimError(caught instanceof ApiRequestError ? caught.message : 'Could not claim that test.');
    } finally {
      setClaiming(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteMyTest(pendingDelete.test_id);
      setPendingDelete(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not delete that test.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  if (signedOut) {
    return (
      <main className="pageWrap">
        <h1>Your tests</h1>
        <p className="pageSub">
          Sign in to keep your tests in one place. You never need an account to create one —
          this just means you stop having to keep track of the links yourself.
        </p>
        <a className="buttonPrimary buttonLink" href="/auth/google">
          Sign in with Google
        </a>
        <p className="mutedNote" style={{ marginTop: 16 }}>
          Reviewers never sign in. Nothing about the watching experience changes.
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pageWrap">
        <h1>Your tests</h1>
        <p className="pageSub">{error}</p>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pageWrap">
        <p className="pageSub">Loading…</p>
      </main>
    );
  }

  return (
    <main className="pageWrap pageWrapWide">
      <div className="myHeader">
        <div>
          <h1>Your tests</h1>
          <p className="pageSub">{data.creator.display_name ?? data.creator.email}</p>
        </div>
        <button
          className="buttonSecondary"
          type="button"
          onClick={() => void postSignOut().then(() => window.location.assign('/'))}
        >
          Sign out
        </button>
      </div>

      <a className="buttonPrimary buttonLink" href="/create">
        New test
      </a>

      {data.tests.length === 0 ? (
        <p className="mutedNote" style={{ marginTop: 20 }}>
          Nothing here yet. Create a test, or claim one you made earlier using its dashboard link below.
        </p>
      ) : (
        <ul className="myList">
          {data.tests.map((test) => (
            <li key={test.test_id}>
              {/* Public thumbnail endpoint — no API key, no extra service. */}
              <img
                className="myThumb"
                src={`https://i.ytimg.com/vi/${test.youtube_id}/mqdefault.jpg`}
                alt=""
                width={120}
                height={68}
                loading="lazy"
              />
              <div className="myMeta">
                <b>{test.title ?? test.test_id}</b>
                <span className="mutedNote">
                  {formatDate(test.created_at)} · {test.reviewer_count}{' '}
                  {test.reviewer_count === 1 ? 'reviewer' : 'reviewers'} · {test.finished_count} finished
                </span>
              </div>
              <div className="myActions">
                <a className="buttonSecondary" href={`/d/${test.dashboard_key}`}>
                  Dashboard
                </a>
                <ShareLinkCopier testId={test.test_id} />
                <button
                  className="buttonDangerSmall"
                  type="button"
                  onClick={() => setPendingDelete(test)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2 className="mySection">Claim an earlier test</h2>
      <p className="mutedNote">
        Made a test before signing in? Paste its dashboard link and it joins this list.
      </p>
      <form onSubmit={(event) => void claim(event)} className="myClaim">
        <input
          type="text"
          placeholder="Paste the dashboard link"
          autoComplete="off"
          value={claimKey}
          onChange={(event) => setClaimKey(event.target.value)}
        />
        <button className="buttonPrimary" type="submit" disabled={claiming || !claimKey.trim()}>
          {claiming ? 'Claiming…' : 'Claim'}
        </button>
      </form>
      {claimed ? <div className="callout">Claimed “{claimed}”. It is now in your list.</div> : null}
      {claimError ? <div className="calloutError">{claimError}</div> : null}

      {/* Cowork015 §2.2 — deleting a test destroys other people's contributions,
          not just the creator's row, so the confirmation states the real numbers
          rather than a bare "are you sure". Not a typed confirmation: that is
          right for an admin destroying someone else's account and
          disproportionate for a creator deleting their own test. */}
      {pendingDelete ? (
        <ConfirmDeleteDialog
          title={`Delete "${pendingDelete.title ?? pendingDelete.test_id}"?`}
          detail={
            pendingDelete.reaction_count === 0
              ? 'Nobody has reacted to this yet. This cannot be undone.'
              : `This also deletes ${pendingDelete.reaction_count} ` +
                `${pendingDelete.reaction_count === 1 ? 'reaction' : 'reactions'} from ` +
                `${pendingDelete.reviewer_count} ` +
                `${pendingDelete.reviewer_count === 1 ? 'person' : 'people'}. This cannot be undone.`
          }
          busy={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}

      <footer className="pageFooter">
        <a href="/privacy">Privacy</a>
      </footer>
    </main>
  );
}

// The share link is what a creator sends to friends; the dashboard link is the
// secret. Only the share link gets a copy button here — the dashboard is one
// click away on the row itself.
function ShareLinkCopier(input: { testId: string }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/t/${input.testId}`;

  return (
    <>
      <button
        className="buttonSecondary"
        type="button"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(shareUrl)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1600);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? 'Copied' : 'Copy share link'}
      </button>
    </>
  );
}
